import React, { useState, useEffect } from 'react';
import { buildApiUrl } from '../config/api';
import { useDashboardStore } from '../store/dashboardStore';

interface LastUpdateInfo {
    date: string;
    recordsProcessed: number;
}

export const DataUpdateDropdown: React.FC = () => {
    const [lastUpdate, setLastUpdate] = useState<LastUpdateInfo | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [datasets, setDatasets] = useState<any[]>([]);
    const { setFilters } = useDashboardStore();

    useEffect(() => {
        fetchLastUpdate();
        fetchDatasets();
    }, []);

    const fetchLastUpdate = async () => {
        try {
            const response = await fetch(buildApiUrl('/api/analytics/last-update'));
            const result = await response.json();

            if (result.success && result.lastUpdate) {
                setLastUpdate(result.lastUpdate);
            }
        } catch (error) {
            console.error('Error fetching last update:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDatasets = async () => {
        try {
            const res = await fetch(buildApiUrl('/api/analytics/datasets'));
            const json = await res.json();
            if (json.success) setDatasets(json.datasets || []);
        } catch (e) {
            console.error('Error fetching datasets:', e);
        }
    };

    const selectDataset = (tag: string) => {
        // Set quarter filter if tag looks like QX_YYYY; otherwise clear
        if (/^Q[1-4]_20\d{2}$/i.test(tag)) {
            setFilters({ quarter: tag.toUpperCase() });
        } else {
            setFilters({ quarter: null });
        }
        // Notify all views to refresh
        window.dispatchEvent(new CustomEvent('refreshAnalytics'));
        setIsOpen(false);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    if (loading) {
        return (
            <div className="relative">
                <div className="flex items-center space-x-2 px-3 py-2 bg-gray-100 rounded-lg">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                    <span className="text-sm text-gray-600">Loading...</span>
                </div>
            </div>
        );
    }

    if (!lastUpdate) {
        return (
            <div className="relative">
                <div className="flex items-center space-x-2 px-3 py-2 bg-gray-100 rounded-lg">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-gray-600">No data uploaded</span>
                </div>
            </div>
        );
    }

    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center space-x-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors border border-blue-200"
            >
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium text-blue-700">
                    Data Updated: {formatDate(lastUpdate.date)}
                </span>
                <svg
                    className={`w-4 h-4 text-blue-600 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-4">
                        <div className="flex items-center space-x-2 mb-3">
                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <h3 className="font-semibold text-gray-900">Data Information</h3>
                        </div>

                        <div className="space-y-3">
                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                <span className="text-sm text-gray-600">Last Updated:</span>
                                <span className="text-sm font-medium text-gray-900">
                                    {formatDateTime(lastUpdate.date)}
                                </span>
                            </div>

                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                <span className="text-sm text-gray-600">Records Processed:</span>
                                <span className="text-sm font-medium text-gray-900">
                                    {lastUpdate.recordsProcessed.toLocaleString()}
                                </span>
                            </div>

                            <div className="flex justify-between items-center py-2">
                                <span className="text-sm text-gray-600">Status:</span>
                                <div className="flex items-center space-x-1">
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    <span className="text-sm font-medium text-green-700">Up to date</span>
                                </div>
                            </div>
                        </div>

                        {datasets.length > 0 && (
                            <div className="mt-4 pt-3 border-t border-gray-100">
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Datasets</h4>
                                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                                    {datasets.map((ds, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => selectDataset(ds.tag)}
                                            className="w-full flex items-center justify-between px-3 py-2 text-left text-sm rounded hover:bg-gray-50"
                                        >
                                            <div>
                                                <div className="font-medium text-gray-900">{ds.tag}</div>
                                                <div className="text-xs text-gray-500">{new Date(ds.date).toLocaleDateString()}</div>
                                            </div>
                                            <div className="text-xs text-gray-500">{ds.records_processed} rows</div>
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                    Selecting a dataset will refresh Overview, Analytics, Charts, and AI.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Backdrop to close dropdown */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsOpen(false)}
                />
            )}
        </div>
    );
};